/**
 * Enhanced Conditional Rendering Engine
 * Supports API state-driven conditional logic for dynamic form components
 */

export interface ConditionalRule {
  field: string;
  operator: 'equals' | 'notEquals' | 'greaterThan' | 'lessThan' | 'contains' | 'in' | 'notIn' | 'exists' | 'isEmpty';
  value?: any;
  values?: any[]; // For 'in' and 'notIn' operators
}

export interface ConditionalLogic {
  // Simple condition (backward compatible)
  field?: string;
  value?: any;
  
  // API state condition
  apiField?: string;
  status?: 'idle' | 'loading' | 'success' | 'error';
  dataPath?: string; // JSONPath to specific data property
  dataOperator?: 'exists' | 'notExists' | 'equals' | 'contains'; // Operator for data path evaluation
  
  // Complex conditions (auto-detected by presence of these properties)
  operator?: 'AND' | 'OR';
  conditions?: ConditionalRule[];
  
  // Nested logic support
  groups?: ConditionalLogic[];
}

/**
 * Represents the status of an API call for form field operations.
 * 
 * @interface APIStatus
 * @property {string} status - Current state of the API call
 * @property {any} [data] - Response data when status is 'success'
 * @property {string} [error] - Error message when status is 'error'  
 * @property {Record<string, any>} [metadata] - Additional API metadata (headers, timing, etc.)
 */
export interface APIStatus {
  /** Current state of the API call */
  status: 'idle' | 'loading' | 'success' | 'error';
  /** Response data when API call succeeds */
  data?: any;
  /** Error message when API call fails */
  error?: string;
  /** Additional metadata like headers, timing info, etc. */
  metadata?: Record<string, any>;
}

/**
 * Context object containing all data needed for conditional rendering evaluation.
 * 
 * @interface ConditionalContext
 * @property {Record<string, any>} formValues - Current form field values
 * @property {Record<string, APIStatus>} apiStates - API call statuses keyed by field name
 * @property {string} [fieldPath] - Optional path to current field for nested evaluation
 */
export interface ConditionalContext {
  formValues: Record<string, any>;
  apiStatuses: Record<string, APIStatus>;
  fieldPath?: string;
}

/**
 * Enhanced Conditional Renderer with API State Support
 * Auto-detects condition types and evaluates complex logic
 */
export class ConditionalRenderer {
  static evaluateCondition(
    condition: ConditionalLogic | undefined,
    context: ConditionalContext
  ): boolean {
    if (!condition) return true;
    
    // Auto-detect: Simple condition (backward compatible)
    if (condition.field && condition.value !== undefined && !condition.apiField) {
      return context.formValues[condition.field] === condition.value;
    }
    
    // Auto-detect: API state condition
    if (condition.apiField) {
      return this.evaluateAPIStatusCondition(condition, context);
    }
    
    // Auto-detect: Complex condition
    if (condition.conditions || condition.groups) {
      return this.evaluateComplexCondition(condition, context);
    }
    
    return true;
  }
  
  private static evaluateComplexCondition(
    condition: ConditionalLogic,
    context: ConditionalContext
  ): boolean {
    const { operator = 'AND', conditions = [], groups = [] } = condition;
    
    // Evaluate direct conditions
    const conditionResults = conditions.map(rule => 
      this.evaluateRule(rule, context.formValues)
    );
    
    // Evaluate nested groups
    const groupResults = groups.map(group => 
      this.evaluateCondition(group, context)
    );
    
    // Combine all results
    const allResults = [...conditionResults, ...groupResults];
    
    if (allResults.length === 0) return true;
    
    if (operator === 'OR') {
      return allResults.some(result => result);
    } else {
      return allResults.every(result => result);
    }
  }
  
  private static evaluateAPIStatusCondition(
    condition: ConditionalLogic,
    context: ConditionalContext
  ): boolean {
    if (!condition.apiField) return false;
    
    const apiState = context.apiStatuses[condition.apiField];
    if (!apiState) return false;
    
    // Check status match
    if (condition.status && apiState.status !== condition.status) {
      return false;
    }
    
    // Check data path if specified
    if (condition.dataPath && condition.dataOperator) {
      const data = this.getDataByPath(apiState.data, condition.dataPath);
      
      switch (condition.dataOperator) {
        case 'exists':
          return data !== undefined && data !== null;
        case 'notExists':
          return data === undefined || data === null;
        case 'equals':
          return data === condition.value;
        case 'contains':
          return Array.isArray(data) ? data.includes(condition.value) : false;
        default:
          return false;
      }
    }
    
    return true;
  }
  
  private static evaluateRule(
    rule: ConditionalRule,
    formValues: Record<string, any>
  ): boolean {
    const fieldValue = formValues[rule.field];
    
    switch (rule.operator) {
      case 'equals':
        return fieldValue === rule.value;
      case 'notEquals':
        return fieldValue !== rule.value;
      case 'greaterThan':
        return Number(fieldValue) > Number(rule.value);
      case 'lessThan':
        return Number(fieldValue) < Number(rule.value);
      case 'contains':
        return String(fieldValue).includes(String(rule.value));
      case 'in':
        return rule.values?.includes(fieldValue) ?? false;
      case 'notIn':
        return !(rule.values?.includes(fieldValue) ?? true);
      case 'exists':
        return fieldValue !== undefined && fieldValue !== null && fieldValue !== '';
      case 'isEmpty':
        return fieldValue === undefined || fieldValue === null || fieldValue === '';
      default:
        return false;
    }
  }
  
  private static getDataByPath(data: any, path: string): any {
    // Simple JSONPath implementation for basic data access
    if (path.startsWith('$.')) {
      const keys = path.slice(2).split('.');
      let result = data;
      for (const key of keys) {
        result = result?.[key];
        if (result === undefined) break;
      }
      return result;
    }
    return data?.[path];
  }
}
